import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

const FortuneRecordDetail = () => {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [record, setRecord] = useState(null);

  useEffect(() => {
    const fetchFortuneRecord = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      if (!recordId) {
        setError('记录ID不存在');
        setLoading(false);
        return;
      }

      try {
        const db = getFirestore();
        const recordDoc = await getDoc(doc(db, 'fortuneReadings', recordId));

        if (!recordDoc.exists()) {
          setError('找不到该算命记录');
          setLoading(false);
          return;
        }

        const recordData = recordDoc.data();
        
        // 验证该记录属于当前用户
        if (recordData.userId !== currentUser.uid) {
          setError('您无权查看此记录');
          setLoading(false);
          return;
        }

        setRecord({
          id: recordDoc.id,
          ...recordData,
          createdAt: recordData.createdAt?.toDate() || new Date()
        });
      } catch (err) {
        console.error('获取算命记录失败:', err);
        setError('获取记录数据失败');
      } finally {
        setLoading(false);
      }
    };

    fetchFortuneRecord();
  }, [recordId, currentUser, navigate]);

  const handleBack = () => {
    navigate('/user');
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          返回个人中心
        </Button>
      </Container>
    );
  }

  if (!record) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">记录不存在或已被删除</Alert>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          返回个人中心
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button 
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        返回个人中心
      </Button>

      <Typography variant="h4" gutterBottom>算命记录详情</Typography>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>八字信息</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">年:</Typography>
              <Typography variant="body1">{record.input?.year || '未知'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">月:</Typography>
              <Typography variant="body1">{record.input?.month || '未知'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">日:</Typography>
              <Typography variant="body1">{record.input?.day || '未知'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="textSecondary">时:</Typography>
              <Typography variant="body1">{record.input?.hour || '未知'}</Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>算命结果</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {record.result || '无结果数据'}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="body2" color="textSecondary">
            记录时间: {record.createdAt.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            记录编号: {record.id}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default FortuneRecordDetail;